import { ErrorHelper, RichTextHelper } from '@helpers';
import { UnweightedScoreRow } from '@sections/UnweightedValuesTable/_components/Columns/Columns';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

interface RiskMetricResultsApi {
  apiRiskMetricResultsPartialUpdate: (params: {
    id: number;
    patchedRiskMetricResult: { comment: string };
  }) => Promise<unknown>;
}

interface UseRiskMetricCommentsParams {
  api: RiskMetricResultsApi;
  updateRowComment: (row: UnweightedScoreRow, updatedComment: string) => void;
  onCommentChange?: (riskMetricResultId: number, comment: string) => void;
}

export const useRiskMetricComments = ({ api, updateRowComment, onCommentChange }: UseRiskMetricCommentsParams) => {
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [activeCommentRow, setActiveCommentRow] = useState<UnweightedScoreRow | null>(null);
  const [commentModalMode, setCommentModalMode] = useState<'add' | 'edit'>('add');
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [commentAction, setCommentAction] = useState<'save' | null>(null);
  const [isDeletingComment, setIsDeletingComment] = useState(false);

  const closeCommentModal = useCallback(() => {
    setCommentAction(null);
    setShowCommentModal(false);
  }, []);

  const openCommentModal = useCallback((row: UnweightedScoreRow) => {
    setActiveCommentRow(row);
    setCommentText(row.comment || '');
    setCommentModalMode(RichTextHelper.hasPlainText(row.comment) ? 'edit' : 'add');
    setShowCommentModal(true);
  }, []);

  const saveComment = useCallback(async () => {
    if (!activeCommentRow) {
      return;
    }

    const nextComment = RichTextHelper.normalizeComment(commentText);

    if (!activeCommentRow.riskMetricResultId) {
      toast.error('No metric result found for this row.');
      return;
    }

    setIsSavingComment(true);
    setCommentAction('save');
    try {
      await api.apiRiskMetricResultsPartialUpdate({
        id: activeCommentRow.riskMetricResultId,
        patchedRiskMetricResult: { comment: nextComment },
      });
      updateRowComment(activeCommentRow, nextComment);
      onCommentChange?.(activeCommentRow.riskMetricResultId, nextComment);
      setShowCommentModal(false);
    } catch (error) {
      ErrorHelper.handleErrorNotification(error);
    } finally {
      setIsSavingComment(false);
      setCommentAction(null);
    }
  }, [activeCommentRow, api, commentText, onCommentChange, updateRowComment]);

  const deleteComment = useCallback(
    async (row: UnweightedScoreRow) => {
      if (!row.riskMetricResultId) {
        toast.error('No metric result found for this row.');
        return;
      }

      setIsDeletingComment(true);
      try {
        await api.apiRiskMetricResultsPartialUpdate({
          id: row.riskMetricResultId,
          patchedRiskMetricResult: { comment: '' },
        });
        updateRowComment(row, '');
        onCommentChange?.(row.riskMetricResultId, '');

        if (activeCommentRow?.riskMetricResultId === row.riskMetricResultId) {
          setCommentText('');
          setShowCommentModal(false);
          setCommentAction(null);
        }
      } catch (error) {
        ErrorHelper.handleErrorNotification(error);
      } finally {
        setIsDeletingComment(false);
      }
    },
    [activeCommentRow?.riskMetricResultId, api, onCommentChange, updateRowComment],
  );

  const isSaveDisabled = RichTextHelper.normalizeComment(commentText) === '';

  return {
    showCommentModal,
    activeCommentRow,
    commentModalMode,
    commentText,
    isSaveDisabled,
    isSavingComment,
    commentAction,
    isDeletingComment,
    setCommentText,
    openCommentModal,
    closeCommentModal,
    saveComment,
    deleteComment,
  };
};
